import {
  compact,
  identity,
  includes,
  isEmpty,
  keyBy,
  pickBy,
  map,
  toLower,
} from "lodash/fp";
import { DateTime } from "luxon";

import type {
  ActionItemFeedItem,
  FeedItemErrorReportType,
  FeedItemReportType,
  FeedItemVideoClip,
  KeyQuestionAnswerFeedItem,
  KeyQuestionFeedItem,
  MeetingReportErrorFeedItem,
  MeetingReportFeedItem,
} from "@app/analytics/Home/types";
import {
  PROD_API_SAMPLE_MEETING,
  STAGING_API_SAMPLE_MEETING,
} from "@app/analytics/sampleData/sampleMeetings";
import type {
  ApiActionItemClip,
  ApiGptHighlight,
  ApiKeyQuestionAnswerClip,
  ApiSessionsListEntity,
  ApiSessionsListEntityWithSummaryFields,
  User,
} from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
} from "@app/utils/utils";

/** The report types that will show as a "Reporting error" notification instead of as a meeting report detail */
const ERROR_REPORT_TYPES: FeedItemErrorReportType[] = [
  "incomplete",
  "data_deleted",
  "out_of_quota",
  "lite",
];

const SAMPLE_MEETING =
  import.meta.env.VITE_APP_STAGE === "production"
    ? PROD_API_SAMPLE_MEETING
    : STAGING_API_SAMPLE_MEETING;

const createVideoClip = ({
  clipStart,
  clipEnd,
}: {
  clipStart?: DateTime;
  clipEnd?: DateTime;
}): FeedItemVideoClip | undefined => {
  if (!clipStart || !clipEnd) return;

  // buffer around start and end of clip to ensure we're not cutting anything off
  return {
    startTime: clipStart.minus({ second: 2 }),
    endTime: clipEnd.plus({ second: 2 }),
  };
};

export const getFormattedMeetingReportDate = (date: DateTime) => {
  return `${date.toLocaleString({
    month: "short",
    day: "numeric",
    year: "numeric",
  })} @ ${toLower(date.toFormat("h:mm a"))}`;
};

const isErrorReportType = (
  type: FeedItemReportType
): type is FeedItemErrorReportType => includes(type, ERROR_REPORT_TYPES);

const getMeetingReportType = (
  session: ApiSessionsListEntity
): FeedItemReportType => {
  if (SessionUtils.isNotetaker(session)) return "notetaker";
  if (SessionUtils.isUploadedSession(session)) return "uploaded";
  if (SessionUtils.isDataDeleted(session)) return "data_deleted";
  if (SessionUtils.isRestricted(session)) return "out_of_quota";
  if (SessionUtils.isReadLite(session)) return "lite";
  return "full";
};

const getErrorMeetingReportText = (
  session: ApiSessionsListEntity,
  reportType: FeedItemErrorReportType
) => {
  switch (reportType) {
    case "incomplete":
      return session.alerts[0]?.message;
    case "data_deleted":
      return "Data for report deleted";
    case "out_of_quota":
      return "You have exceeded your reporting quota for the month. Upgrade your account to generate a report.";
    case "lite":
      return "Read was not given recording permission by the meeting host.";
    default:
      return null;
  }
};

const parseErrorMeetingReport = (
  session: ApiSessionsListEntity,
  reportType: FeedItemErrorReportType
): MeetingReportErrorFeedItem | null => {
  const errorText = getErrorMeetingReportText(session, reportType);

  // skip this session if we don't know what the error was, since
  // we won't have a useful message to display to the user
  if (!errorText) return null;

  const startTime = parseUTCDateTimeFromString(session.startTime);
  const timestamp =
    reportType === "data_deleted"
      ? parseUTCDateTimeFromString(session.dataExpirationDate!)
      : startTime;

  return {
    type: "meeting_report_error",
    timestamp,
    title: "Reporting Error",
    entityId: session.id,
    session: {
      id: session.id,
      title: session.title,
      startTime,
    },
    text: errorText,
    data: { reportType },
  };
};

const getMeetingReportMetric = <T>(value?: T | null) => {
  if (!value) return;

  if (typeof value === "string") return value as T;
  if (typeof value === "number") return Math.round(value) as T;
};

const getMeetingReportMetricFromList = (value?: string[] | null) => {
  return value ? value.length : undefined;
};

const sanitizeMetrics = (metrics: MeetingReportFeedItem["data"]["metrics"]) =>
  pickBy(identity, metrics);

const parseMeetingReport = (
  session: ApiSessionsListEntityWithSummaryFields,
  isShared = false
): MeetingReportFeedItem | MeetingReportErrorFeedItem | null => {
  const reportType = getMeetingReportType(session);

  if (isErrorReportType(reportType)) {
    return parseErrorMeetingReport(session, reportType);
  }

  const meetingStartTime = parseUTCDateTimeFromString(session.startTime);

  const metrics: MeetingReportFeedItem["data"]["metrics"] = {
    engagement: getMeetingReportMetric(session.metrics.engagement),
    readScore: getMeetingReportMetric(session.metrics.meetingScore),
    sentiment: getMeetingReportMetric(session.metrics.sentiment),
    summary: getMeetingReportMetric(session.summary),
    numActionItems: getMeetingReportMetricFromList(session.actionItems),
    numKeyQuestions: getMeetingReportMetricFromList(session.keyQuestions),
    // only show topic count on notetaker reports, since they won't have a summary or RS/E/S metrics
    numTopics: getMeetingReportMetricFromList(
      reportType === "notetaker" ? session.topics : null
    ),
  };

  const title =
    reportType === "notetaker" ? "Notetaker Report" : "Meeting Report";

  return {
    type: "meeting_report",
    timestamp: meetingStartTime,
    title,
    entityId: session.id,
    session: {
      id: session.id,
      startTime: meetingStartTime,
      title: session.title,
    },
    data: {
      isShared,
      reportType,
      metrics: sanitizeMetrics(metrics),
    },
  };
};

export const parseMeetingReports = (
  sessions: ApiSessionsListEntity[]
): (MeetingReportFeedItem | MeetingReportErrorFeedItem)[] => {
  if (isEmpty(sessions)) return [];

  return compact(sessions.map((s) => parseMeetingReport(s)));
};

export const parseSharedMeetingReports = (
  sharedSessions: ApiSessionsListEntity[],
  sessionsWithDirectAccess: ApiSessionsListEntity[]
): (MeetingReportFeedItem | MeetingReportErrorFeedItem)[] => {
  // there's no way of knowing right now any of the sharing-specific information, so this method's
  // only purpose right now is to simply dedupe meetings that also appeared in the default sessions list response
  if (isEmpty(sharedSessions)) return [];

  const sessionIdsToDedupe = map("id", sessionsWithDirectAccess);
  const parsedSharedSessions = sharedSessions.map((session) => {
    if (sessionIdsToDedupe.includes(session.id)) return null;
    return parseMeetingReport(session, true);
  });

  return compact(parsedSharedSessions);
};

export const parseIncompleteMeetingReports = (
  sessions: ApiSessionsListEntity[]
): MeetingReportErrorFeedItem[] => {
  if (isEmpty(sessions)) return [];

  const parsedSessions = sessions.map((session) =>
    parseErrorMeetingReport(session, "incomplete")
  );
  return compact(parsedSessions);
};

export const parseActionItems = (
  actionItems: ApiActionItemClip[]
): ActionItemFeedItem[] => {
  const actionItemFeedItems = actionItems.map((actionItem) => {
    // ignore any rejected action items
    if (actionItem.status === "user_reject" || actionItem.isDeleted) {
      return null;
    }

    // action item may not have a start time if it was generated manually by a user on the Meeting Reports page
    const actionItemStartTime = optionalParseUTCDateTimeFromString(
      actionItem.startTime
    );

    const sessionStartTime = optionalParseUTCDateTimeFromString(
      actionItem.meetingStartTime
    );

    const timestamp = actionItemStartTime ?? sessionStartTime;

    // if we don't have a timestamp, we won't have any idea where to place this action item
    // in the feed, and so just skip it entirely
    if (!timestamp) return null;

    const session = {
      id: actionItem.sessionId,
      title: actionItem.meetingTitle ?? "",
      startTime: sessionStartTime,
    };

    const videoClip = createVideoClip({
      clipStart: actionItemStartTime,
      clipEnd: optionalParseUTCDateTimeFromString(actionItem.endTime),
    });

    const item: ActionItemFeedItem = {
      type: "action_item",
      title: "Action Item",
      timestamp,
      entityId: actionItem.id,
      session,
      text: actionItem.text,
      videoClip,
      data: {
        ownerUserId: actionItem.ownerUserId,
        canEdit: Boolean(actionItem.canEdit),
        status: actionItem.status ?? "read_inactive",
      },
    };

    return item;
  });

  return compact(actionItemFeedItems);
};

export const parseKeyQuestionsAndAnswers = (
  keyQuestions: ApiGptHighlight[],
  keyQuestionAnswers: ApiKeyQuestionAnswerClip[]
): (KeyQuestionFeedItem | KeyQuestionAnswerFeedItem)[] => {
  const questionsById = keyBy("id", keyQuestions);
  const answersByQuestionId = keyBy("parentClipId", keyQuestionAnswers);

  const answersCoupledWithQuestion: string[] = [];

  const keyQuestionFeedItems = keyQuestions.map((question) => {
    if (question.isDeleted) return null;

    const sessionStartTime = optionalParseUTCDateTimeFromString(
      question.meetingStartTime
    );

    // question may not have a start time if it was generated manually by a user on the Meeting Reports page
    const questionStartTime = optionalParseUTCDateTimeFromString(
      question.startTime
    );

    const timestamp = questionStartTime ?? sessionStartTime;

    // if we don't have a timestamp, we won't have any idea where to place this question
    // in the feed, and so just skip it entirely
    if (!timestamp) return null;

    const session = {
      id: question.sessionId,
      title: question.meetingTitle ?? "",
      startTime: sessionStartTime,
    };

    const answer = answersByQuestionId[question.id];

    if (answer && answer.status !== "user_reject" && !answer.isDeleted) {
      const answerStartTime = parseUTCDateTimeFromString(answer.startTime);

      const minutesBetweenQuestionAndAnswer = answerStartTime
        .diff(timestamp)
        .as("minutes");

      // if the question and the answer happened within 1 minute of each other, we can reasonably assume this is
      // an answer proposed by read, and we will present the question and answer together. otherwise, the question
      // was answered manually by a user and so the question and the answer should appear as separate items
      if (minutesBetweenQuestionAndAnswer < 1) {
        const item: KeyQuestionAnswerFeedItem = {
          type: "key_question_answer",
          title: "Key Question",
          timestamp,
          entityId: answer.id,
          session,
          text: answer.text,
          data: {
            canEdit: Boolean(answer.canEdit),
            question: question.text,
            questionId: answer.parentClipId,
            status: answer.status,
          },
        };
        answersCoupledWithQuestion.push(answer.id);
        return item;
      }
    }

    const videoClip = createVideoClip({
      clipStart: questionStartTime,
      clipEnd: optionalParseUTCDateTimeFromString(question.endTime),
    });

    const item: KeyQuestionFeedItem = {
      type: "key_question",
      title: "Key Question",
      timestamp,
      entityId: question.id,
      session,
      text: question.text,
      videoClip,
      data: {},
    };

    return item;
  });

  const answers = keyQuestionAnswers.map((answer) => {
    if (
      !answer.parentClipId ||
      answer.status === "user_reject" ||
      answersCoupledWithQuestion.includes(answer.id)
    ) {
      return null;
    }

    const questionForAnswer = questionsById[answer.parentClipId];

    // an answer without a question is not worth displaying
    if (!questionForAnswer || questionForAnswer.isDeleted) return null;

    const sessionStartTime = optionalParseUTCDateTimeFromString(
      answer.meetingStartTime
    );

    const answerStartTime = parseUTCDateTimeFromString(answer.startTime);

    const item: KeyQuestionAnswerFeedItem = {
      type: "key_question_answer",
      title: "Key Question Answered",
      timestamp: answerStartTime,
      entityId: answer.id,
      session: {
        id: answer.sessionId,
        title: answer.meetingTitle ?? "",
        startTime: sessionStartTime,
      },
      text: answer.text,
      data: {
        canEdit: Boolean(answer.canEdit),
        question: questionForAnswer.text,
        questionId: answer.parentClipId,
        status: answer.status,
      },
    };

    return item;
  });

  return [...compact(keyQuestionFeedItems), ...compact(answers)];
};

export const parseSampleReport = (user: User) => {
  const sampleMeeting = {
    ...SAMPLE_MEETING,
    startTime: DateTime.fromMillis(user.createdOn).toISO()!,
  };

  return parseMeetingReport(sampleMeeting);
};
